import React from 'react';

/**
 * What the outermost error boundary shows when the app cannot render.
 *
 * Without a fallback, Sentry's ErrorBoundary returns null, and because this one
 * wraps everything above Provider, Router and Suspense, any uncaught error left
 * the user on a blank white page with no way forward.
 *
 * Styles are inline on purpose. This renders precisely when the app is broken,
 * and a failed CSS chunk is one of the ways it gets here -- a stylesheet this
 * screen depended on could be the very thing that is missing.
 */

const shell: React.CSSProperties = {
  alignItems: 'center',
  background: '#212121',
  color: '#ececec',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  gap: '18px',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px',
  textAlign: 'center',
};

const button: React.CSSProperties = {
  background: '#3b82f6',
  border: 'none',
  borderRadius: '8px',
  color: '#ffffff',
  cursor: 'pointer',
  fontSize: '15px',
  fontWeight: 600,
  padding: '11px 22px',
};

const reload = (): void => {
  window.location.reload();
};

export const AppErrorFallback: React.FC = () => (
  <div style={shell} role="alert">
    <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>This page didn&apos;t load</h1>
    <p style={{ color: '#9e9e9e', fontSize: '15px', margin: 0, maxWidth: '380px' }}>
      Something went wrong while rendering. Reloading usually fixes it — your work is saved.
    </p>
    <button onClick={reload} style={button} type="button">
      Reload the page
    </button>
  </div>
);
